import React, { useEffect, useState } from "react";
import { pluralUA } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import {
    Activity, RotateCw, CheckCircle2, AlertTriangle, XCircle, Info,
    ChevronDown, Clock, Loader2, ArrowRight,
} from "lucide-react";
import { api } from "@/lib/api";

const SEV_ICON = {
    ok: CheckCircle2,
    info: Info,
    warning: AlertTriangle,
    error: XCircle,
};

const SEV_COLOR = {
    ok: "text-success-c",
    info: "text-primary-c",
    warning: "text-warning-c",
    error: "text-error-c",
};

const SEV_BG = {
    ok: "border-success/20 bg-success/5",
    info: "border-primary/20 bg-primary/5",
    warning: "border-warning/30 bg-warning/8",
    error: "border-error/30 bg-error/8",
};

const CATEGORY_LABEL = {
    campaign: "Кампанія",
    budget: "Бюджет",
    targeting: "Таргетинг",
    groups: "Групи оголошень",
    keywords: "Ключові слова",
    ads: "Оголошення",
    extensions: "Розширення",
    conversions: "Конверсії",
    system: "Система",
};

function scoreColor(s) {
    // Нові пороги (PR #7): 5-рівнева градація замість 4.
    //   90+   Чудово         (зелений)    — стратегія виконана повністю
    //   75-89 Добре          (синій)      — невеликі недоробки
    //   60-74 Потребує уваги (жовтий)     — щось пропущено
    //   40-59 Слабо          (помаранчевий)— багато невиконаного
    //   <40   Критично       (червоний)   — критичні провали
    if (s >= 90) return { text: "text-success-c", bg: "bg-success", label: "Чудово" };
    if (s >= 75) return { text: "text-primary-c", bg: "bg-primary", label: "Добре" };
    if (s >= 60) return { text: "text-warning-c", bg: "bg-warning", label: "Потребує уваги" };
    if (s >= 40) return { text: "text-warning-c", bg: "bg-warning", label: "Слабо" };
    return { text: "text-error-c", bg: "bg-error", label: "Критично" };
}

export default function HealthCheckCard() {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [error, setError] = useState("");

    const load = async () => {
        try {
            const r = await api.get("/health/latest");
            if (r.data?.available) setReport(r.data);
        } catch {}
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const runNow = async () => {
        setRunning(true);
        setError("");
        try {
            const r = await api.post("/health/run-check");
            setReport(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Не вдалося запустити перевірку");
        } finally {
            setRunning(false);
        }
    };

    if (loading) {
        return (
            <section className="glass rounded-2xl p-7" data-testid="health-card-loading">
                <div className="flex items-center gap-3 text-secondary-c">
                    <Loader2 size={16} className="animate-spin" />
                    <span className="text-sm font-mono">Завантажуємо health check…</span>
                </div>
            </section>
        );
    }

    if (!report) {
        return (
            <section className="glass rounded-2xl p-7" data-testid="health-card-empty">
                <div className="flex items-start gap-3">
                    <Activity size={20} className="text-primary-c mt-1 shrink-0" />
                    <div className="flex-1">
                        <h2 className="font-display font-bold text-xl text-text-primary">Health Check кампанії</h2>
                        <p className="mt-2 text-sm text-secondary-c">
                            Запустіть перевірку, щоб AutoAds опитав Google Ads і звірив ваше налаштування зі стратегією — знайдемо проблеми ще до того, як вони з'їдять бюджет.
                        </p>
                        {error && <div className="mt-3 text-sm text-error-c font-mono" data-testid="health-error">{error}</div>}
                        <button
                            onClick={runNow}
                            disabled={running}
                            className={`mt-5 btn-primary ${running ? "opacity-50 cursor-not-allowed" : "pulse-glow"}`}
                            data-testid="health-run-btn"
                        >
                            {running ? <Loader2 size={14} className="animate-spin" /> : <Activity size={14} />}
                            {running ? "Перевіряємо…" : "Запустити перевірку"}
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    const score = report.score || 0;
    const sc = scoreColor(score);
    const summary = report.summary || {};
    const checks = report.checks || [];
    const live = report.live_metrics || {};
    const createdAt = report.created_at ? new Date(report.created_at) : null;
    const ageMin = createdAt ? Math.floor((Date.now() - createdAt.getTime()) / 60000) : null;
    const ageLabel = ageMin == null ? "" :
        ageMin < 1 ? "щойно" :
        ageMin < 60 ? `${ageMin} хв тому` :
        ageMin < 1440 ? `${Math.floor(ageMin / 60)} год тому` :
        `${Math.floor(ageMin / 1440)} д тому`;

    // Group checks by category
    const byCategory = checks.reduce((acc, c) => {
        const cat = c.category || "system";
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(c);
        return acc;
    }, {});
    const categoryOrder = ["campaign", "budget", "targeting", "groups", "keywords", "ads", "extensions", "conversions", "system"];

    return (
        <section className="glass rounded-2xl p-7" data-testid="health-card">
            <div className="flex items-start gap-4 flex-wrap">
                <Activity size={22} className="text-primary-c mt-1 shrink-0" />
                <div className="flex-1 min-w-[200px]">
                    <h2 className="font-display font-bold text-xl text-text-primary">Health Check кампанії</h2>
                    <p className="mt-1 text-xs text-faint-c font-mono">
                        Перевірено {ageLabel}
                    </p>
                </div>
                <button
                    onClick={runNow}
                    disabled={running}
                    className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-mono px-3 py-2 rounded-md border border-border text-secondary-c hover:border-primary/40 hover:text-text-primary transition ${running ? "opacity-50" : ""}`}
                    data-testid="health-recheck-btn"
                >
                    {running ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />}
                    Перевірити заново
                </button>
            </div>

            {error && <div className="mt-3 text-sm text-error-c font-mono" data-testid="health-error">{error}</div>}

            {report.error ? (
                <div className="mt-5 flex items-start gap-3 p-4 rounded-lg border border-error/30 bg-error/8">
                    <XCircle size={18} className="text-error-c mt-0.5 shrink-0" />
                    <div className="text-sm text-text-primary">
                        <div className="font-semibold">Не вдалося прочитати акаунт</div>
                        <div className="mt-1 text-secondary-c font-mono text-xs break-words">{report.error}</div>
                    </div>
                </div>
            ) : (
                <>
                    {/* Score block */}
                    <div className="mt-6 flex items-center gap-6 flex-wrap" data-testid="health-score-block">
                        <div className="flex items-baseline gap-2">
                            <span className={`font-display font-extrabold text-5xl tabular-nums ${sc.text}`} data-testid="health-score">
                                {score}
                            </span>
                            <span className="text-secondary-c font-mono text-sm">/ 100</span>
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <div className={`text-sm font-semibold ${sc.text}`}>{sc.label}</div>
                            <div className="mt-2 h-2 w-full bg-surface rounded-full overflow-hidden border border-border">
                                <div
                                    className={`h-full ${sc.bg} transition-[width] duration-700 ease-out`}
                                    style={{ width: `${score}%` }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Severity counters */}
                    <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="health-summary">
                        <SummaryPill label="OK" count={summary.ok || 0} severity="ok" />
                        <SummaryPill label="Інфо" count={summary.info || 0} severity="info" />
                        <SummaryPill label="Уваги" count={summary.warning || 0} severity="warning" />
                        <SummaryPill label="Помилок" count={summary.error || 0} severity="error" />
                    </div>

                    {/* Live metrics row */}
                    <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-mono">
                        <Metric forms={["кампанія", "кампанії", "кампаній"]} value={live.campaigns} />
                        <Metric forms={["група", "групи", "груп"]} value={live.ad_groups} />
                        <Metric forms={["ключ", "ключі", "ключів"]} value={live.keywords} />
                        <Metric forms={["мінус-слово", "мінус-слова", "мінус-слів"]} value={live.neg_keywords} />
                        <Metric forms={["оголошення", "оголошення", "оголошень"]} value={live.ads} />
                        <Metric label="sitelinks" value={live.sitelinks} />
                        <Metric label="callouts" value={live.callouts} />
                        <Metric forms={["конверсія", "конверсії", "конверсій"]} value={live.conversion_actions} />
                    </div>

                    {/* Checks */}
                    <div className="mt-6">
                        <button
                            onClick={() => setExpanded((v) => !v)}
                            className="w-full flex items-center justify-between gap-3 p-3 rounded-md border border-border hover:border-primary/30 transition"
                            data-testid="health-toggle-checks"
                        >
                            <span className="text-sm font-mono text-text-primary">
                                Деталі перевірок ({checks.length})
                            </span>
                            <ChevronDown size={14} className={`text-secondary-c transition-transform ${expanded ? "rotate-180" : ""}`} />
                        </button>

                        {expanded && (
                            <div className="mt-3 space-y-4" data-testid="health-checks-list">
                                {categoryOrder.filter(c => byCategory[c]?.length).map((cat) => (
                                    <div key={cat}>
                                        <div className="text-[11px] font-mono uppercase tracking-wider text-faint-c mb-2">
                                            {CATEGORY_LABEL[cat] || cat}
                                        </div>
                                        <div className="space-y-2">
                                            {byCategory[cat].map((c) => <CheckRow key={c.id} check={c} />)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </section>
    );
}

const SummaryPill = ({ label, count, severity }) => (
    <div
        className={`flex items-center justify-between px-3 py-2 rounded-md border ${SEV_BG[severity]}`}
        data-testid={`health-summary-${severity}`}
    >
        <span className={`text-xs font-mono uppercase tracking-wider ${SEV_COLOR[severity]}`}>{label}</span>
        <span className={`font-display font-bold text-base tabular-nums ${SEV_COLOR[severity]}`}>{count}</span>
    </div>
);

const Metric = ({ label, forms, value }) => {
    // `forms` (preferred) is a [singular, few, many] triplet → grammatically
    // correct UA plural. `label` is the legacy single-form fallback.
    const displayLabel = forms && Array.isArray(forms)
        ? pluralUA(value || 0, forms)
        : label;
    return (
        <span className="px-2 py-1 rounded border border-border text-secondary-c">
            <span className="text-text-primary">{value ?? "—"}</span> {displayLabel}
        </span>
    );
};

const CheckRow = ({ check }) => {
    const Icon = SEV_ICON[check.severity] || Info;
    const navigate = useNavigate();

    // PR #8: action — структуроване посилання на /strategy?tab=wizard&campaign=N
    const handleAction = () => {
        const a = check.action;
        if (!a) return;
        if (a.target === "wizard") {
            const idx = a.params?.campaign_idx ?? 0;
            navigate(`/strategy?tab=wizard&campaign=${idx}`);
        } else if (a.target === "strategy") {
            navigate("/strategy");
        }
    };

    return (
        <div
            className={`flex items-start gap-3 p-3 rounded-lg border ${SEV_BG[check.severity]}`}
            data-testid={`health-check-${check.id}`}
        >
            <Icon size={16} className={`${SEV_COLOR[check.severity]} mt-0.5 shrink-0`} />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-text-primary">{check.title}</div>
                {check.message && (
                    <div className="mt-1 text-xs text-text-primary/85 leading-relaxed">{check.message}</div>
                )}
                {(check.expected || check.actual) && (
                    <div className="mt-1.5 flex flex-wrap gap-2 text-[10px] font-mono">
                        {check.expected && (
                            <span className="px-1.5 py-0.5 rounded border border-border text-faint-c">
                                очікувалось: <span className="text-secondary-c">{check.expected}</span>
                            </span>
                        )}
                        {check.actual && (
                            <span className="px-1.5 py-0.5 rounded border border-border text-faint-c">
                                фактично: <span className="text-text-primary">{check.actual}</span>
                            </span>
                        )}
                    </div>
                )}
                {/* PR #8: справжня кнопка дії (якщо backend повернув structured action) */}
                {check.action && (
                    <button
                        type="button"
                        onClick={handleAction}
                        className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                                   bg-primary/15 hover:bg-primary/25 border border-primary/30
                                   text-primary-c text-xs font-semibold transition-colors
                                   focus:outline-none focus:ring-2 focus:ring-primary/40"
                        data-testid={`health-check-action-${check.id}`}
                    >
                        <span>{check.action.label}</span>
                        <ArrowRight size={12} />
                    </button>
                )}
                {/* Текстова підказка — показуємо тільки якщо нема structured action */}
                {!check.action && check.action_hint && (
                    <div className="mt-2 flex items-start gap-1.5 text-[11px] text-secondary-c">
                        <Clock size={10} className="mt-0.5 shrink-0" />
                        <span>{check.action_hint}</span>
                    </div>
                )}
            </div>
        </div>
    );
};